import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { NoData } from 'components/NoData'
import { tokens } from 'styling/tokens'
import { RootState } from 'store'
import { BarFill, BarLabel, BarRow, BarTrack, BarValue } from 'pages/HrDashboard/hrLayout.styled'
import { IOpsHandoverIncompleteTask, IOpsLiveTask } from 'types/operationsControl.interface'
import { IOpsRequest } from 'types/opsRequests.interface'
import { ALERT_SEVERITY_META, taskCoreState, USE_OPS_MOCKS } from '../Shared/opsControl'
import { OPS_HANDOVER } from '../Shared/opsControlMocks'
import { deptName } from '../Shared/opsDepartments'
import { REQUEST_PRIORITY_META, REQUEST_STATUS_META, requestSlaLabel, requestSlaState, SLA_TONE } from '../Shared/opsRequestsData'
import { CoreStatePill, DemoBadge, SourceBadge } from '../Shared/OpsSharedComponents'
import {
    DeltaCard,
    DeltaGrid,
    DeltaValue,
    FieldLabel,
    HandoverGrid,
    MachineDownMeta,
    MachineDownRow,
    RequestCode,
    RiskDot,
    RiskList,
    RiskRow,
    SeverityRow,
    SignOffActions,
    SignOffConfirmed,
    SpanFull,
    SubtitleRow,
    TaskMetricsGrid,
    UtilList,
} from './ShiftHandover.styled'

const incompleteColumns: IHonestTableColumn<IOpsHandoverIncompleteTask>[] = [
    {
        id: 'task',
        header: 'Task',
        cell: (item) => <BodyText weight='600'>{item.name}</BodyText>,
    },
    {
        id: 'location',
        header: 'Location',
        cell: (item) => <BodyText>{item.location}</BodyText>,
    },
    {
        id: 'source',
        header: 'Source',
        cell: (item) => <SourceBadge source={item.source} />,
    },
    {
        id: 'reason',
        header: 'Reason',
        cell: (item) => <OtherText grey>{item.reason || '—'}</OtherText>,
    },
]

const reactiveColumns: IHonestTableColumn<IOpsLiveTask>[] = [
    {
        id: 'task',
        header: 'Task',
        cell: (item) => <BodyText weight='600'>{item.name}</BodyText>,
    },
    {
        id: 'machine',
        header: 'Machine',
        cell: (item) => <BodyText>{item.machine || '—'}</BodyText>,
    },
    {
        id: 'operator',
        header: 'Operator',
        cell: (item) => <BodyText>{item.operator || '—'}</BodyText>,
    },
    {
        id: 'status',
        header: 'Status',
        cell: (item) => <CoreStatePill state={taskCoreState(item.taskStatus)} context='task' />,
    },
    {
        id: 'location',
        header: 'Location',
        cell: (item) => <BodyText>{item.location}</BodyText>,
    },
]

const requestColumns: IHonestTableColumn<IOpsRequest>[] = [
    {
        id: 'request',
        header: 'Request',
        cell: (item) => (
            <Flex column gap='0.25rem'>
                <RequestCode>{item.code}</RequestCode>
                <BodyText weight='600'>{item.title}</BodyText>
            </Flex>
        ),
    },
    {
        id: 'route',
        header: 'Route',
        cell: (item) => (
            <BodyText>
                {deptName(item.fromDept)} → {deptName(item.toDept)}
            </BodyText>
        ),
    },
    {
        id: 'priority',
        header: 'Priority',
        cell: (item) => (
            <StatusPill status={REQUEST_PRIORITY_META[item.priority].tone}>
                {REQUEST_PRIORITY_META[item.priority].label}
            </StatusPill>
        ),
    },
    {
        id: 'neededBy',
        header: 'Needed by',
        cell: (item) =>
            item.status === 'done' || item.status === 'declined' ? (
                <OtherText grey>—</OtherText>
            ) : (
                <StatusPill status={SLA_TONE[requestSlaState(item)]}>{requestSlaLabel(item)}</StatusPill>
            ),
    },
    {
        id: 'endOfShiftState',
        header: 'End-of-shift state',
        cell: (item) => (
            <Flex column gap='0.25rem'>
                <Flex center gap='0.5rem'>
                    <StatusPill status={REQUEST_STATUS_META[item.status].tone}>
                        {REQUEST_STATUS_META[item.status].label}
                    </StatusPill>
                    {item.escalationLevel > 0 && <StatusPill status='danger'>Escalated</StatusPill>}
                </Flex>
                {item.status === 'declined' && item.declineReason && <OtherText grey>{item.declineReason}</OtherText>}
            </Flex>
        ),
    },
]

export const ShiftHandover = () => {
    const navigate = useNavigate()
    const [acknowledged, setAcknowledged] = useState(false)
    const { requests } = useSelector((state: RootState) => state.opsRequests)
    const report = OPS_HANDOVER
    const completionPct = report.tasksPlanned > 0 ? Math.round((report.tasksCompleted / report.tasksPlanned) * 100) : 0

    const handleAcknowledge = () => {
        setAcknowledged(true)
        toast.success('Handover acknowledged')
    }

    if (!USE_OPS_MOCKS) {
        return (
            <>
                <NavBar />
                <PageWrapper>
                    <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>Shift handover</Head1>
                    <NoData text='Handover reports are generated at shift boundaries once the backend is connected.' />
                </PageWrapper>
            </>
        )
    }

    return (
        <>
            <NavBar />
            <PageWrapper>
                <Flex center gap='0.75rem'>
                    <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>Shift handover</Head1>
                    <DemoBadge />
                    <Button variant='black' size='sm' onClick={() => window.print()}>
                        Print / PDF
                    </Button>
                </Flex>
                <SubtitleRow>
                    <BodyText grey>{report.shiftName} · {report.shiftDate} · generated {report.generatedAt}</BodyText>
                    <OtherText grey>Locks automatically 2 hours after shift end</OtherText>
                </SubtitleRow>
                <HandoverGrid>
                    <SpanFull>
                        <Container>
                            <ContainerHeader>
                                <TitleText>Plan progress this shift</TitleText>
                            </ContainerHeader>
                            <ContainerContent>
                                <DeltaGrid>
                                    {report.planDeltas.map((d) => (
                                        <DeltaCard key={d.id}>
                                            <BodyText weight='600'>{d.planName}</BodyText>
                                            <DeltaValue>{d.deltaThisShift}</DeltaValue>
                                            <OtherText grey>{d.totalProgress}</OtherText>
                                            <BarTrack>
                                                <BarFill $pct={d.progressPercent} />
                                            </BarTrack>
                                        </DeltaCard>
                                    ))}
                                </DeltaGrid>
                            </ContainerContent>
                        </Container>
                    </SpanFull>
                    <SpanFull>
                        <Container>
                            <ContainerHeader>
                                <TitleText>Tasks</TitleText>
                            </ContainerHeader>
                            <ContainerContent>
                                <TaskMetricsGrid>
                                    <MetricCard label='PLANNED' value={report.tasksPlanned} accentColor={tokens.status.info.fg} />
                                    <MetricCard
                                        label='COMPLETED'
                                        value={report.tasksCompleted}
                                        accentColor={tokens.status.success.fg}
                                        trailing={<StatusPill status='success'>{completionPct}%</StatusPill>}
                                    />
                                    <MetricCard label='CYCLES COMPLETED' value={report.cyclesCompleted} accentColor={tokens.accent.base} />
                                </TaskMetricsGrid>
                            </ContainerContent>
                        </Container>
                    </SpanFull>
                    <SpanFull>
                        <Container>
                            <ContainerHeader>
                                <TitleText>Not finished this shift</TitleText>
                            </ContainerHeader>
                            <ContainerContent>
                                <HonestTable columns={incompleteColumns} data={report.incompleteTasks} keyField='id' />
                            </ContainerContent>
                        </Container>
                    </SpanFull>
                    <SpanFull>
                        <Container>
                            <ContainerHeader>
                                <TitleText>Still being handled</TitleText>
                            </ContainerHeader>
                            <ContainerContent>
                                <HonestTable columns={reactiveColumns} data={report.openReactiveTasks} keyField='id' onClick={(item) => navigate(`/operations/task/${item.id}`)} />
                            </ContainerContent>
                        </Container>
                    </SpanFull>
                    <SpanFull>
                        <Container>
                            <ContainerHeader padding>
                                <Flex column gap='0.25rem'>
                                    <TitleText>Requests</TitleText>
                                    <BodyText grey>Cross-department asks this shift — the incoming supervisor inherits the open ones.</BodyText>
                                </Flex>
                            </ContainerHeader>
                            <ContainerContent>
                                <HonestTable
                                    columns={requestColumns}
                                    data={requests}
                                    keyField='id'
                                    emptyMessage='No cross-department requests this shift.'
                                />
                            </ContainerContent>
                        </Container>
                    </SpanFull>
                    <Container>
                        <ContainerHeader>
                            <TitleText>Machines down</TitleText>
                        </ContainerHeader>
                        <ContainerContent gap='0.75rem'>
                            {report.machinesDown.map((m) => (
                                <MachineDownRow key={m.id}>
                                    <BodyText weight='600'>{m.name}</BodyText>
                                    <MachineDownMeta>
                                        <Flex column gap='0.25rem'>
                                            <FieldLabel>Since</FieldLabel>
                                            <BodyText>{m.since}</BodyText>
                                        </Flex>
                                        <Flex column gap='0.25rem'>
                                            <FieldLabel>Repair status</FieldLabel>
                                            <BodyText>{m.repairStatus}</BodyText>
                                        </Flex>
                                        <Flex column gap='0.25rem'>
                                            <FieldLabel>Estimated return</FieldLabel>
                                            <BodyText>{m.estimatedReturn}</BodyText>
                                        </Flex>
                                    </MachineDownMeta>
                                </MachineDownRow>
                            ))}
                        </ContainerContent>
                    </Container>
                    <Container>
                        <ContainerHeader>
                            <TitleText>Alerts raised</TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            <SeverityRow>
                                {report.alertsBySeverity.map((a) => (
                                    <StatusPill key={a.severity} status={ALERT_SEVERITY_META[a.severity].tone}>
                                        {ALERT_SEVERITY_META[a.severity].label} · {a.count}
                                    </StatusPill>
                                ))}
                            </SeverityRow>
                        </ContainerContent>
                    </Container>
                    <Container>
                        <ContainerHeader>
                            <TitleText>How busy we were</TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            <UtilList>
                                <BarRow>
                                    <BarLabel>Worker</BarLabel>
                                    <BarTrack>
                                        <BarFill $pct={report.workerUtilizationPct} />
                                    </BarTrack>
                                    <BarValue>{report.workerUtilizationPct}%</BarValue>
                                </BarRow>
                                <BarRow>
                                    <BarLabel>Machine</BarLabel>
                                    <BarTrack>
                                        <BarFill $pct={report.machineUtilizationPct} $color={tokens.status.info.fg} />
                                    </BarTrack>
                                    <BarValue>{report.machineUtilizationPct}%</BarValue>
                                </BarRow>
                                <BarRow>
                                    <BarLabel>Idle</BarLabel>
                                    <BarTrack>
                                        <BarFill $pct={report.idleTimePct} $color={tokens.status.warning.fg} />
                                    </BarTrack>
                                    <BarValue>{report.idleTimePct}%</BarValue>
                                </BarRow>
                            </UtilList>
                        </ContainerContent>
                    </Container>
                    <Container>
                        <ContainerHeader>
                            <TitleText>Next shift risks</TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            <RiskList>
                                {report.nextShiftRisks.map((risk, i) => (
                                    <RiskRow key={i}>
                                        <RiskDot color={tokens.status.warning.fg} />
                                        <BodyText>{risk}</BodyText>
                                    </RiskRow>
                                ))}
                            </RiskList>
                        </ContainerContent>
                    </Container>
                    <SpanFull>
                        <Container>
                            <ContainerHeader>
                                <TitleText>Incoming supervisor sign-off</TitleText>
                            </ContainerHeader>
                            <ContainerContent gap='0.75rem'>
                                {acknowledged ? (
                                    <SignOffConfirmed>
                                        <BodyText weight='600' color={tokens.status.success.fg}>✓ Acknowledged by Milos Preview · 18:12</BodyText>
                                        <OtherText grey>Sign-off is recorded to the audit trail once the backend is connected.</OtherText>
                                    </SignOffConfirmed>
                                ) : (
                                    <>
                                        <BodyText grey>The incoming supervisor confirms they have read this handover. Unsigned handovers are flagged in the audit trail.</BodyText>
                                        <SignOffActions>
                                            <Button onClick={handleAcknowledge}>Acknowledge handover</Button>
                                        </SignOffActions>
                                    </>
                                )}
                            </ContainerContent>
                        </Container>
                    </SpanFull>
                </HandoverGrid>
            </PageWrapper>
        </>
    )
}
